// client/src/designTokens.ts

/**
 * ===================================================================================
 * Design Token System
 * ===================================================================================
 * This file is the single source of truth for all design tokens in the application,
 * aligning with the Catalyst Design System documented in Confluence.
 *
 * --- Token Categories ---
 *
 * 1. Primitive Tokens:
 *    These are the foundational, context-agnostic values for visual design elements.
 *    They represent raw values like specific hex codes for colors, pixel values for
 *    spacing, font weights, etc. They don't carry semantic meaning on their own.
 *    Examples: color_brand_primary: "#0090E3", spacing_md: "8px".
 *
 * 2. Semantic Tokens (UI-Wide):
 *    These tokens assign a specific meaning or contextual role to primitive tokens.
 *    They describe the intended purpose of a style in the broader UI, rather than
 *    a specific component.
 *    Examples: color_text_primary: "var(--color_neutral_black)",
 *              color_bg_surface_standard: "var(--color_neutral_white)".
 *
 * 3. Component-Specific Semantic Tokens:
 *    These tokens define the styling for specific parts of individual UI components
 *    and their various states. They typically map to primitive or UI-wide semantic tokens.
 *    They follow the naming convention: `scope_component_property_element_state`
 *    (or `scope_component_property_state` if element variant is part of component name).
 *
 *    Naming Convention Parts:
 *    - <scope>: The design domain (e.g., color, radius, spacing, font).
 *    - <component>: The UI component (e.g., button, cardProduct).
 *    - <property>: The part of the component being styled (e.g., bg, text, border, icon).
 *    - <element>: (Optional) The variant or specific element within the component (e.g., primary for buttons).
 *                 Often incorporated into the <component> name (e.g., button_primary).
 *    - <state>: The UI state (e.g., enabled, hover, active, disabled, selected).
 *    Examples: color_button_bg_primary_enabled: "var(--color_brand_3)",
 *              radius_button_standard: "var(--radius_xs)".
 *
 * --- Usage ---
 *
 * - Primitives are defined directly.
 * - Semantic and Component-Specific tokens often reference other tokens using CSS
 *   custom property syntax (e.g., "var(--some_other_token)").
 * - The `getTokenValue` function can resolve these references.
 * - The `AG3Theme` object in `theme.ts` maps these token names to a structured
 *   theme object for consumption by styled-components.
 * - `DynamicGlobalStyle` in `App.tsx` injects primitive tokens as CSS custom
 *   properties into the :root, making them globally available.
 * ===================================================================================
 */

// Helper to ensure pixel values are strings with "px"
const px = (value: number | string): string => {
  if (
    typeof value === "number" ||
    (typeof value === "string" && /^[0-9.]+$/.test(value))
  ) {
    return `${value}px`;
  }
  return value.toString(); // For font names, existing "0rem", or pre-formatted values
};

// --- Primitive Tokens ---

export const fontFamilyTokens = {
  font_family_primary: "Inter",
  font_family_secondary: "Inter",
};

export const fontWeightTokens = {
  font_weight_light: "300",
  font_weight_regular: "400",
  font_weight_medium: "500",
  font_weight_semibold: "600",
  font_weight_bold: "700",
  font_weight_extrabold: "800",
};

export const fontSizeTokens = {
  font_size_xs: px(10),
  font_size_sm: px(12),
  font_size_md: px(14),
  font_size_base: px(16),
  font_size_xl: px(20),
  font_size_2xl: px(24),
  font_size_3xl: px(32),
  font_size_4xl: px(40),
  font_size_5xl: px(48),
  font_size_6xl: px(56),
};

export const lineHeightTokens = {
  line_height_xs: px(12),
  line_height_sm: px(16),
  line_height_md: px(20),
  line_height_base: px(24),
  line_height_xl: px(28),
  line_height_2xl: px(32),
  line_height_3xl: px(40),
  line_height_4xl: px(48),
  line_height_5xl: px(56),
  line_height_6xl: px(64),
};

export const colorPrimitiveTokens = {
  // Neutral
  color_neutral_white: "#FFFFFF",
  color_neutral_gray100: "#F9FAFB",
  color_neutral_gray200: "#E7E8EA",
  color_neutral_gray300: "#CFD1D4",
  color_neutral_gray400: "#9FA4AA",
  color_neutral_gray500: "#6F777F",
  color_neutral_gray600: "#3F4A55",
  color_neutral_black: "#0F1D2B",
  // Brand
  color_brand_primary: "#0090E3",
  color_brand_3: "#0090E308",
  color_brand_5: "#0090E30D",
  color_brand_10: "#0090E31A",
  color_brand_15: "#0090E326",
  color_brand_mixed: "#f7fbfe", // 3% brand mixed with white - computed dynamically
  // Error
  color_error_dark: "#CB2020",
  color_error_light: "#FDF8F8",
  // Success
  color_success_dark: "#1FB45A",
  color_success_light: "#F4FBF7",
  // Warning
  color_warning_dark: "#DB5B16",
  color_warning_light: "#FDF7F3",
  // Caution
  color_caution_dark: "#F6B200",
  color_caution_light: "#FFFDF7",
  // Info
  color_info_dark: "#0090E3",
  color_info_light: "#F7FCFE",
  // Custom
  color_custom_1: "#3968FF",
  color_custom_2: "#9E77ED",
  color_custom_3: "#66C61C",
  color_custom_4: "#EE46BC",
};

export const spacingTokens = {
  spacing_none: px(0),
  spacing_xxs: px(2),
  spacing_xs: px(4),
  spacing_sm: px(6),
  spacing_md: px(8),
  spacing_lg: px(12),
  spacing_xl: px(16),
  spacing_2xl: px(20),
  spacing_3xl: px(24),
  spacing_4xl: px(32),
  spacing_5xl: px(40),
  spacing_6xl: px(48),
  spacing_7xl: px(64),
  spacing_8xl: px(80),
  spacing_9xl: px(96),
  spacing_10xl: px(128),
};
export const containerSettingsTokens = {
  no_containers: "No Containers",
  individual_containers: "Individual Containers",
  single_container: "Single Container",
};

export const radiusTokens = {
  radius_none: px(0),
  radius_xxs: px(2),
  radius_xs: px(4),
  radius_sm: px(6),
  radius_md: px(8),
  radius_lg: px(10),
  radius_xl: px(12),
  radius_2xl: px(16),
  radius_3xl: px(20),
  radius_4xl: px(24),
  radius_5xl: px(32),
};

export const borderPrimitiveTokens = {
  border_none: px(0),
  border_xxs: px(0.25),
  border_xs: px(0.5),
  border_sm: px(0.75),
  border_md: px(1),
  border_lg: px(1.25),
  border_xl: px(1.5),
  border_2xl: px(2),
  border_3xl: px(4),
  border_custom_1: px(6),
  border_custom_2: px(8),
};

export const widthTokens = {
  width_xxxs: px(240),
  width_xxs: px(320),
  width_xs: px(480),
  width_sm: px(640),
  width_md: px(768),
  width_lg: px(1024),
  width_xl: px(1280),
  width_2xl: px(1536),
};

// --- Semantic Tokens ---

export const colorTextSemanticTokens = {
  color_text_primary: "var(--color_neutral_black)",
  color_text_secondary: "var(--color_neutral_gray600)",
  color_text_tertiary: "var(--color_neutral_gray500)",
  color_text_placeholder: "var(--color_neutral_gray400)",
  color_text_error: "var(--color_error_dark)",
  color_text_warning: "var(--color_warning_dark)",
  color_text_caution: "var(--color_caution_dark)",
  color_text_success: "var(--color_success_dark)",
  color_text_info: "var(--color_info_dark)",
  color_text_inverse: "var(--color_neutral_white)",
  color_text_brand: "var(--color_brand_primary)",
};

export const colorBgUiSemanticTokens = {
  color_bg_surface_standard: "var(--color_neutral_white)",
  color_bg_surface_subtle: "var(--color_neutral_gray100)",
  color_bg_surface_dark: "var(--color_neutral_gray600)",
  color_bg_surface_darkest: "var(--color_neutral_black)",
  color_bg_surface_disabled: "var(--color_neutral_gray100)",
  color_bg_surface_disabled_dark: "var(--color_neutral_gray300)",
  color_bg_surface_disabled_darker: "var(--color_neutral_gray400)",
  color_bg_surface_error: "var(--color_error_light)",
  color_bg_surface_error_dark: "var(--color_error_dark)",
  color_bg_surface_warning: "var(--color_warning_light)",
  color_bg_surface_warning_dark: "var(--color_warning_dark)",
  color_bg_surface_caution: "var(--color_caution_light)",
  color_bg_surface_caution_dark: "var(--color_caution_dark)",
  color_bg_surface_success: "var(--color_success_light)",
  color_bg_surface_success_dark: "var(--color_success_dark)",
  color_bg_surface_info: "var(--color_info_light)",
  color_bg_surface_info_dark: "var(--color_info_dark)",
  color_bg_surface_brand: "var(--color_brand_primary)",
  color_bg_surface_brand3: "var(--color_brand_3)",
  color_bg_surface_brand5: "var(--color_brand_5)",
  color_bg_surface_brand10: "var(--color_brand_10)",
  color_bg_surface_hover: "var(--color_brand_3)",
  color_bg_surface_brand_mixed: "var(--color_brand_mixed)",
  color_bg_surface_custom1: "#F6F5F5",
  color_bg_surface_custom2: "#F9FAFA",
  color_bg_overlay: "#0F1D2B99",
};

export const colorBorderSemanticTokens = {
  color_border_lightest: "var(--color_neutral_gray100)",
  color_border_lighter: "var(--color_neutral_gray200)",
  color_border_light: "var(--color_neutral_gray300)",
  color_border_dark: "var(--color_neutral_gray400)",
  color_border_darker: "var(--color_neutral_gray500)",
  color_border_darkest: "var(--color_neutral_gray600)",
  color_border_brand: "var(--color_brand_primary)",
  color_border_success: "var(--color_success_dark)",
  color_border_error: "var(--color_error_dark)",
  color_border_warning: "var(--color_warning_dark)",
  color_border_info: "var(--color_info_dark)",
  color_border_caution: "var(--color_caution_dark)",
  color_border_input: "var(--color_neutral_gray300)",
  color_border_input_active: "var(--color_neutral_black)",
  color_border_input_error: "var(--color_error_dark)",
  color_border_input_warning: "var(--color_warning_dark)",
  color_border_input_caution: "var(--color_caution_dark)",
};

export const colorIconSemanticTokens = {
  color_icon_primary: "var(--color_neutral_black)",
  color_icon_secondary: "var(--color_neutral_gray600)",
  color_icon_tertiary: "var(--color_neutral_gray500)",
  color_icon_disabled: "var(--color_neutral_gray400)",
  color_icon_white: "var(--color_neutral_white)",
  color_icon_success: "var(--color_success_dark)",
  color_icon_error: "var(--color_error_dark)",
  color_icon_warning: "var(--color_warning_dark)",
  color_icon_caution: "var(--color_caution_dark)",
  color_icon_info: "var(--color_info_dark)",
  color_icon_brand: "var(--color_brand_primary)",
};

// --- Component Specific Semantic Tokens (Buttons) ---
// Naming: scope_component_property_element_state (element often part of component, e.g., button_primary)
export const buttonSemanticTokens = {
  // Shared Button Tokens (Retained for now, may need full Confluence alignment later)
  button_font_family: "var(--font_family_primary)",
  button_font_weight_regular: "var(--font_weight_regular)",
  button_font_weight_medium: "var(--font_weight_medium)",
  button_font_size_base: "var(--font_size_base)",
  button_line_height_base: "var(--line_height_base)",
  button_padding_base: "var(--spacing_md) var(--spacing_xl)",
  button_gap: "var(--spacing_md)",
  button_transition: "all 0.2s ease-in-out", // Not a token per Confluence spec
  button_border_width: "1px", // Not a token per Confluence spec
  button_loading_icon_size: "15px", // Not a token per Confluence spec

  radius_button_standard: "var(--radius_xs)",

  // the following three exist on figma but not on confluence

  // Button Size Tokens - Large
  button_font_size_large: "var(--font_size_base)",
  button_line_height_large: "var(--line_height_base)",
  button_padding_large: "var(--spacing_lg) var(--spacing_xl)",

  // Button Size Tokens - Medium
  button_font_size_medium: "var(--font_size_md)",
  button_line_height_medium: "var(--line_height_md)",
  button_padding_medium: "var(--spacing_lg) var(--spacing_xl)",

  // Button Size Tokens - Small
  button_font_size_small: "var(--font_size_sm)",
  button_line_height_small: "var(--line_height_sm)",
  button_padding_small: "var(--spacing_sm) var(--spacing_md)",

  // Primary Button
  color_button_bg_primary_enabled: "var(--color_brand_3)",
  color_button_bg_primary_hover: "var(--color_brand_5)",
  color_button_bg_primary_active: "var(--color_brand_10)",
  color_button_bg_primary_disabled: "var(--color_brand_3)",
  color_button_text_primary_enabled: "var(--color_neutral_black)",
  color_button_text_primary_hover: "var(--color_neutral_black)",
  color_button_text_primary_active: "var(--color_neutral_black)",
  color_button_text_primary_disabled: "var(--color_neutral_black)",
  color_button_border_primary_enabled: "var(--color_brand_primary)",
  color_button_border_primary_hover: "var(--color_brand_primary)",
  color_button_border_primary_active: "var(--color_brand_primary)",
  color_button_border_primary_disabled: "var(--color_brand_primary)",
  color_button_icon_primary_enabled: "var(--color_neutral_black)",
  // Add _hover, _active, _disabled for icon if needed, Confluence has them separately. For now, using enabled as base.

  // Secondary Button
  color_button_bg_secondary_enabled: "var(--color_neutral_gray100)",
  color_button_bg_secondary_hover: "var(--color_neutral_gray100)",
  color_button_bg_secondary_active: "var(--color_neutral_gray100)",
  color_button_bg_secondary_disabled: "var(--color_neutral_gray100)",
  color_button_text_secondary_enabled: "var(--color_neutral_black)",
  color_button_text_secondary_hover: "var(--color_neutral_black)",
  color_button_text_secondary_active: "var(--color_neutral_black)",
  color_button_text_secondary_disabled: "var(--color_neutral_black)",
  color_button_border_secondary_enabled: "var(--color_neutral_gray100)",
  color_button_border_secondary_hover: "var(--color_neutral_gray200)",
  color_button_border_secondary_active: "var(--color_neutral_gray300)",
  color_button_border_secondary_disabled: "var(--color_neutral_gray100)",
  color_button_icon_secondary_enabled: "var(--color_neutral_black)",

  // Tertiary Button
  color_button_bg_tertiary_enabled: "var(--color_neutral_white)",
  color_button_bg_tertiary_hover: "var(--color_neutral_white)",
  color_button_bg_tertiary_active: "var(--color_brand_3)",
  color_button_bg_tertiary_disabled: "var(--color_neutral_white)",
  color_button_text_tertiary_enabled: "var(--color_neutral_gray600)",
  color_button_text_tertiary_hover: "var(--color_neutral_black)",
  color_button_text_tertiary_active: "var(--color_neutral_black)",
  color_button_text_tertiary_disabled: "var(--color_neutral_gray600)",
  color_button_border_tertiary_enabled: "var(--color_neutral_gray200)",
  color_button_border_tertiary_hover: "var(--color_neutral_gray200)",
  color_button_border_tertiary_active: "var(--color_brand_primary)",
  color_button_border_tertiary_disabled: "var(--color_neutral_gray200)",
  color_button_icon_tertiary_enabled: "var(--color_neutral_black)",

  // Destructive Button
  color_button_bg_destructive_enabled: "var(--color_neutral_white)",
  color_button_bg_destructive_hover: "var(--color_neutral_white)",
  color_button_bg_destructive_active: "var(--color_error_dark)",
  color_button_bg_destructive_disabled: "var(--color_neutral_white)",
  color_button_text_destructive_enabled: "var(--color_error_dark)",
  color_button_text_destructive_hover: "var(--color_error_dark)",
  color_button_text_destructive_active: "var(--color_neutral_white)",
  color_button_text_destructive_disabled: "var(--color_error_dark)",
  color_button_border_destructive_enabled: "var(--color_error_dark)",
  color_button_border_destructive_hover: "var(--color_error_dark)",
  color_button_border_destructive_active: "var(--color_error_dark)",
  color_button_border_destructive_disabled: "var(--color_error_dark)",
  color_button_icon_destructive_enabled: "var(--color_error_dark)",
  color_button_icon_destructive_hover: "var(--color_error_dark)",

  // Ghost Button
  color_button_bg_ghost_enabled: "var(--color_neutral_white)",
  color_button_bg_ghost_hover: "var(--color_neutral_white)",
  color_button_bg_ghost_active: "var(--color_brand_5)",
  color_button_bg_ghost_disabled: "var(--color_neutral_white)",
  color_button_text_ghost_enabled: "var(--color_neutral_gray600)",
  color_button_text_ghost_hover: "var(--color_neutral_gray600)",
  color_button_text_ghost_active: "var(--color_neutral_black)",
  color_button_text_ghost_disabled: "var(--color_neutral_gray600)",
  color_button_border_ghost_enabled: "var(--color_neutral_white)",
  color_button_border_ghost_hover: "var(--color_neutral_gray200)",
  color_button_border_ghost_active: "transparent",
  color_button_border_ghost_disabled: "var(--color_neutral_white)",
  color_button_icon_ghost_enabled: "var(--color_neutral_black)",

  // Link Button
  color_button_textLink_enabled: "var(--color_neutral_black)",
  color_button_textLink_hover: "var(--color_brand_primary)",
  color_button_textLink_disabled: "var(--color_neutral_black)",

  // Link Destructive
  color_button_textLink_destructive_enabled: "var(--color_error_dark)",
  color_button_textLink_destructive_hover: "var(--color_error_dark)",
  color_button_textLink_destructive_disabled: "var(--color_error_dark)",
};

// --- Component Specific Semantic Tokens (ButtonIcon) ---
// Confluence: color_buttonIcon_bg_enabled, color_buttonIcon_icon_enabled etc.
export const buttonIconSemanticTokens = {
  // Background Colors
  color_buttonIcon_bg_enabled: "var(--color_neutral_white)",
  color_buttonIcon_bg_hover: "var(--color_brand_3)",
  color_buttonIcon_bg_disabled: "var(--color_neutral_white)",
  color_buttonIcon_bg_destructive_enabled: "var(--color_neutral_white)",
  color_buttonIcon_bg_destructive_hover: "var(--color_neutral_white)",
  color_buttonIcon_bg_destructive_disabled: "var(--color_neutral_white)",
  // Border Colors
  color_buttonIcon_border_enabled: "var(--color_neutral_white)",
  color_buttonIcon_border_hover: "var(--color_brand_primary)",
  color_buttonIcon_border_disabled: "var(--color_neutral_white)",
  color_buttonIcon_border_destructive_enabled: "var(--color_neutral_white)",
  color_buttonIcon_border_destructive_hover: "var(--color_error_dark)",
  color_buttonIcon_border_destructive_disabled: "var(--color_neutral_white)",
  // Radius
  radius_buttonIcon_standard: "var(--radius_xs)",
  // Icon Colors
  color_buttonIcon_icon_enabled: "var(--color_neutral_black)",
  color_buttonIcon_icon_hover: "var(--color_neutral_black)",
  color_buttonIcon_icon_disabled: "var(--color_neutral_black)",
  color_buttonIcon_icon_destructive_enabled: "var(--color_error_dark)",
  color_buttonIcon_icon_destructive_hover: "var(--color_error_dark)",
  color_buttonIcon_icon_destructive_disabled: "var(--color_error_dark)",
  // Link
  color_button_icon_textLink_enabled: "var(--color_neutral_black)",
  color_button_icon_textLink_hover: "var(--color_brand_primary)",
  color_button_icon_textLink_disabled: "var(--color_neutral_black)",
  // Link Destructive
  color_button_icon_destructive: "var(--color_error_dark)",
};

// --- Component Specific Semantic Tokens (CardProduct) ---
// Naming follows scope_component_property_state
export const cardProductSemanticTokens = {
  // Text Colors
  color_cardProduct_text_product_name: "var(--color_text_primary)",
  color_cardProduct_text_attribute_name: "var(--color_text_tertiary)",
  color_cardProduct_text_attribute_value: "var(--color_text_primary)",

  // Background & Border
  color_cardProduct_bg: "var(--color_bg_surface_standard)",
  color_cardProduct_border: "var(--color_border_lighter)",
  color_cardProduct_border_hover: "var(--color_border_brand)",

  // Border Widths (individual sides allow asymmetric theming)
  border_cardProduct_top: "var(--border_md)",
  border_cardProduct_right: "var(--border_md)",
  border_cardProduct_bottom: "var(--border_md)",
  border_cardProduct_left: "var(--border_md)",
  // Hover Border Widths (maintain same width on hover for now, allows future customization)
  border_cardProduct_top_hover: "var(--border_md)",
  border_cardProduct_right_hover: "var(--border_md)",
  border_cardProduct_bottom_hover: "var(--border_md)",
  border_cardProduct_left_hover: "var(--border_md)",

  // Radii
  radius_cardProduct: "var(--radius_md)",
  radius_cardProduct_image: "var(--radius_xs)",
  radius_cardProduct_image_none: "var(--radius_none)",

  // Spacing / Gaps
  spacing_cardProduct_gap: "var(--spacing_md)",
  spacing_cardProduct_image_padding: "var(--spacing_none)",

  // Icon Colors
  color_cardProduct_icon: "var(--color_icon_secondary)",

  // Aspect Ratios (using native CSS aspect-ratio property)
  // NOT on figma
  aspect_cardProduct_1_1: "1 / 1", // 1:1 square
  aspect_cardProduct_3_2: "3 / 2", // 3:2 landscape
  aspect_cardProduct_16_9: "16 / 9", // 16:9 widescreen
  aspect_cardProduct_default: "3 / 2", // Default aspect ratio to use (3:2)

  // Shadow Effect (expressed as individual primitives for flexibility)
  effect_cardProduct_shadow_x: "0", // px
  effect_cardProduct_shadow_y: "1px", // token could reference spacing_xxs but keeping px literal per design spec
  effect_cardProduct_shadow_blur: "3px",
  effect_cardProduct_shadow_spread: "0",
  effect_cardProduct_shadow_color: "#0000000F", // 6% black per design
};

// --- Component Specific Semantic Tokens (CardProduct) ---
// Naming follows scope_component_property_state
export const filterPanelSemanticTokens = {
  // Background & Border
  color_filterSearchBar_bg: "var(--color_bg_surface_standard)",
  color_filterGroup_bg_collpased: "var(--color_bg_surface_standard)",
  color_filterGroup_bg_expanded: "var(--color_bg_surface_standard)",

  color_filterSearchBar_border: "var(--color_border_lighter)",
  color_filterGroup_border_collpased: "var(--color_border_lighter)",
  color_filterGroup_border_expanded: "var(--color_border_lighter)",
  color_filterPanel_border: "var(--color_border_lighter)",

  // Border Widths (individual sides allow asymmetric theming)
  border_filterGroup: "var(--border_md)",
  border_filterPanel: "var(--border_md)",

  // Spacing / Gaps
  spacing_filterItem_gap: "var(--spacing_md)",
  spacing_filterPanel: "var(--spacing_md)",
};

export const productDetailSemanticTokens = {
  color_bg_surface_product: "var(--color_bg_surface_standard)",
  container_settings: "no_containers",
  spacing_productDetail_tabList: "var(--spacing_xl)",
  spacing_productDetail_tabPanel: "var(--spacing_xl)",
  radius_productDetail_card: "var(--radius_md)",
};

// --- Strict Token Types ---

// Font Family Token Types
type FontFamilyToken = keyof typeof fontFamilyTokens;

// Font Weight Token Types
type FontWeightToken = keyof typeof fontWeightTokens;

// Font Size Token Types
type FontSizeToken = keyof typeof fontSizeTokens;

// Line Height Token Types
type LineHeightToken = keyof typeof lineHeightTokens;

// Color Primitive Token Types
type ColorPrimitiveToken = keyof typeof colorPrimitiveTokens;

// Spacing Token Types
type SpacingToken = keyof typeof spacingTokens;

// Container Settings Token Types
type ContainerSettingsToken = keyof typeof containerSettingsTokens;

// Radius Token Types
type RadiusToken = keyof typeof radiusTokens;

// Border Primitive Token Types
type BorderPrimitiveToken = keyof typeof borderPrimitiveTokens;

// Width Token Types
type WidthToken = keyof typeof widthTokens;

// Color Text Semantic Token Types
type ColorTextSemanticToken = keyof typeof colorTextSemanticTokens;

// Color Background UI Semantic Token Types
type ColorBgUiSemanticToken = keyof typeof colorBgUiSemanticTokens;

// Color Border Semantic Token Types
type ColorBorderSemanticToken = keyof typeof colorBorderSemanticTokens;

// Color Icon Semantic Token Types
type ColorIconSemanticToken = keyof typeof colorIconSemanticTokens;

// Button Semantic Token Types
type ButtonSemanticToken = keyof typeof buttonSemanticTokens;

// Button Icon Semantic Token Types
type ButtonIconSemanticToken = keyof typeof buttonIconSemanticTokens;

// Card Product Semantic Token Types
type CardProductSemanticToken = keyof typeof cardProductSemanticTokens;

// Filter Panel Semantic Token Types
type FilterPanelSemanticToken = keyof typeof filterPanelSemanticTokens;

// Product Detail Semantic Token Types
type ProductDetailSemanticToken = keyof typeof productDetailSemanticTokens;

// Union of all token types
export type AllTokenKeys =
  | FontFamilyToken
  | FontWeightToken
  | FontSizeToken
  | LineHeightToken
  | ColorPrimitiveToken
  | SpacingToken
  | ContainerSettingsToken
  | RadiusToken
  | BorderPrimitiveToken
  | WidthToken
  | ColorTextSemanticToken
  | ColorBgUiSemanticToken
  | ColorBorderSemanticToken
  | ColorIconSemanticToken
  | ButtonSemanticToken
  | ButtonIconSemanticToken
  | CardProductSemanticToken
  | FilterPanelSemanticToken
  | ProductDetailSemanticToken;

// --- Aggregated Tokens & Resolver ---

export const allTokens: Record<AllTokenKeys, string> = {
  ...fontFamilyTokens,
  ...fontWeightTokens,
  ...fontSizeTokens,
  ...lineHeightTokens,
  ...colorPrimitiveTokens,
  ...spacingTokens,
  ...containerSettingsTokens,
  ...radiusTokens,
  ...borderPrimitiveTokens,
  ...widthTokens,
  ...colorTextSemanticTokens,
  ...colorBgUiSemanticTokens,
  ...colorBorderSemanticTokens,
  ...colorIconSemanticTokens,
  ...buttonSemanticTokens,
  ...buttonIconSemanticTokens,
  ...cardProductSemanticTokens,
  ...filterPanelSemanticTokens,
  ...productDetailSemanticTokens,
};

const varRegex = /var\\(--([^)]+)\\)/;

// Improved getTokenValue to prevent excessive console warnings for legitimate direct CSS values
export function getTokenValue(
  tokenNameOrValue: string | undefined,
  tokens: Record<AllTokenKeys, string> = allTokens,
  visited: Set<string> = new Set()
): string {
  if (tokenNameOrValue === undefined) {
    // console.warn("getTokenValue called with undefined tokenNameOrValue");
    return ""; // Or a sensible default, e.g., 'inherit' or throw error
  }

  // If tokenNameOrValue is a direct CSS value (e.g., #fff, 10px, transparent, 0)
  // and not a token name, return it directly.
  // This simple check assumes token names generally don't look like CSS values.
  // A more robust check might be to see if it's NOT in Object.keys(tokens) AND looks like a CSS value.
  if (
    !(tokenNameOrValue in tokens) &&
    (tokenNameOrValue.startsWith("#") ||
      tokenNameOrValue.startsWith("rgb") ||
      /px$|rem$|em$|^0$|transparent$|inherit$|auto$|normal$|bold$|italic$/.test(
        tokenNameOrValue
      ))
  ) {
    return tokenNameOrValue;
  }

  const tokenName = tokenNameOrValue as AllTokenKeys; // Cast to AllTokenKeys since we've checked it exists

  if (visited.has(tokenName)) {
    // console.warn(\`Circular dependency detected for token: ${tokenName}. Path: \${Array.from(visited).join(" -> ")} -> \${tokenName}\`);
    return ""; // Break circular dependency. Fallback to a very basic default.
  }
  visited.add(tokenName);

  let value = tokens[tokenName];

  if (value === undefined) {
    // console.warn(\`Token not found: ${tokenName}. Path: \${Array.from(visited).join(" -> ")}\`);
    // If the original input was not a direct CSS value but also not a token, it's an issue.
    return tokenNameOrValue; // Fallback to the original name/value if not found, maybe it's a direct CSS value missed by the initial check
  }

  let match = varRegex.exec(value);
  while (match) {
    const referencedTokenName = match[1];
    const resolvedPart = getTokenValue(
      referencedTokenName,
      tokens,
      new Set(visited)
    ); // Use new Set for sub-paths to allow same token in different branches
    value = value.replace(match[0], resolvedPart);
    match = varRegex.exec(value); // Re-check for more variables in the same string
  }

  visited.delete(tokenName);
  return value;
}
